import { useEffect, useState } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Drawer } from "@mui/material";
import { ChevronRightIcon } from "@heroicons/react/24/solid";

import CartIcon from "../Icon/Cart";
import WalletIcon from "../Icon/WalletIcon";
import UserIcon from "../Icon/UserIcon";
import Home from "../Icon/Home";
import LogoutIcon from "../Icon/Logout";
import Cross from "../../assets/images/CrossIcon.png";

import {
  WalletPage,
  BookmarksPage,
  ProfilePage,
  AddressPage,
  PastOrdersPage,
  RewardsPage,
  PreferencePage,
  FAQPage,
} from "../../routes/RoutesPath";

import { setLoginModule } from "../../store/userData/userDataSlice";

import LocationIcon from "../Icon/LocationIcon";
import BookmarkIcon from "../Icon/BookMarkIcon";
import PastorderIcon from "../Icon/PastorderIcon";
import MyReward from "../Icon/MyReward";
import HamburgerIcon from "../Icon/Hamburger";
import Cart from "../Elements/Cart";

import { checkEmpty } from "../../utils/helper";
import useHomeClick from "../../hooks/useHomeClick";
import LogoutConfirmModal from "../Modal/LogoutConfirmModal";
import FaqIcon from "@/components/Icon/FaqIcon";
import { AVATAR_DEFAULT_SRC } from "@/constants/assets";

/* ------------------------------------------------------------------
   CONSTANTS (magic strings, labels, keys, urls)
-------------------------------------------------------------------*/

// Menu keys (logic)
const MENU_KEYS = {
  HOME: "HOME",
  CART: "CART",
  PROFILE: "PROFILE",
  FAQ: "FAQ",
};


// UI text / labels
const UI_TEXT = {
  MENU: {
    HOME: "Home",
    CART: "Cart",
    PROFILE: "Profile",
    FAQ: "Help",
  },
  PROFILE_DRAWER: {
    PROFILE_INFO: "Profile Info",
    ADDRESS: "Address",
    BOOKMARKS: "Bookmarks",
    BANK_CARDS: "Bank Cards",
    PAST_ORDERS: "Past Orders",
    MY_REWARDS: "My Rewards",
    PREFERENCE: "Preference",
    WALLET: "Loyalty",
    LOGOUT: "Logout",
    FAQ: "FAQ",
    PRIVACY_POLICY: "Privacy Policy",
  },
  LOGOUT_MODAL: {
    TITLE: "Are you sure you want to log out?",
    CONFIRM: "Logout",
    CANCEL: "Cancel",
  },
  ALT: {
    USER_AVATAR: "User avatar",
    CLOSE_ICON: "Close drawer",
  },
};

// URLs
const FALLBACK_AVATAR_URL = AVATAR_DEFAULT_SRC

/* ------------------------------------------------------------------
   Bottom Menu
-------------------------------------------------------------------*/

const BottomMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggedIn = useSelector((state) => state.userdata.loggedIn);
  const userDetails = useSelector((state) => state?.basicdata?.customer);
  const basket = useSelector((state) => state?.basicdata?.basket);
  const locationData = useSelector((state) => state?.basicdata?.locationData);
  const websiteColor = useSelector((state) => state?.onLoadData?.color);

  const [menuActive, setMenuActive] = useState(MENU_KEYS.HOME);
  const [totalItems, setTotalItems] = useState(0);

  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const { handleHomeRedirect } = useHomeClick();

  // derive active menu from URL (on mount + route change)
  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const segments = path.split("/");

    if (segments.includes("profile")) {
      setMenuActive(MENU_KEYS.PROFILE);
    } else if (segments.includes("faq")) {
      setMenuActive(MENU_KEYS.FAQ);
    } else if (segments.includes("stores")) {
      setMenuActive(MENU_KEYS.HOME);
    }
  }, [location.pathname]);

  // total cart items
  useEffect(() => {
    let count = 0;
    basket?.basketProducts?.forEach((val) => {
      count += val.pos_basketproduct_Quantity;
    });
    setTotalItems(count);
  }, [basket]);

  const openLoginModal = () => {
    dispatch(setLoginModule(true));
  };

  const toggleCartDrawer = () => {
    setIsCartDrawerOpen((prev) => !prev);
  };

  const closeProfileDrawer = () => {
    setIsProfileDrawerOpen(false);
  };

  const openLogoutModal = () => {
    closeProfileDrawer();
    setIsLogoutModalOpen(true);
  };

  const goToFaq = () => {
    navigate(FAQPage);
    closeProfileDrawer();
  };

  const handleMenuClick = (key) => {
    switch (key) {
      case MENU_KEYS.HOME: {
        setMenuActive(MENU_KEYS.HOME);

        const pathname = location.pathname.toLowerCase();
        const pathParts = pathname.split("/");
        let postcode = "";

        if (pathParts.includes("stores")) {
          const idx = pathParts.indexOf("stores") + 1;
          postcode = pathParts[idx]?.toLowerCase();
        } else {
          postcode = locationData?.postcodePretty?.toLowerCase();
        }

        if (!checkEmpty(postcode)) {
          handleHomeRedirect(postcode);
        } else {
          navigate("/");
        }
        break;
      }

      case MENU_KEYS.CART:
        setMenuActive(MENU_KEYS.CART);
        toggleCartDrawer();
        break;

      case MENU_KEYS.PROFILE:
        if (isLoggedIn) {
          setMenuActive(MENU_KEYS.PROFILE);
          setIsProfileDrawerOpen(true);
        } else {
          openLoginModal();
        }
        break;

      case MENU_KEYS.FAQ:
        setMenuActive(MENU_KEYS.FAQ);
        navigate(FAQPage);
        break;
    }
  };

  return (
    <div>
      {/* Bottom nav (mobile only) */}
      <div className="h-16 w-full flex mdl:hidden justify-around bg-white rounded-t-lg shadow-black shadow-2xl bottom-0 left-0 fixed z-50 text-xs">
        {/* Cart drawer */}
        <Drawer anchor="right" open={isCartDrawerOpen} onClose={toggleCartDrawer}>
          <Cart isDrawer toggleDrawer={toggleCartDrawer} />
        </Drawer>

        {/* Profile drawer */}
        <Drawer anchor="right" open={isProfileDrawerOpen} onClose={closeProfileDrawer}>
          <ProfileDrawer
            closeDrawer={closeProfileDrawer}
            userDetails={userDetails}
            onLogoutClick={openLogoutModal}
            onFaqClick={goToFaq}
          />
        </Drawer>

        {/* Home */}
        <div
          className="flex flex-col items-center mt-2 cursor-pointer hover:scale-105"
          onClick={() => handleMenuClick(MENU_KEYS.HOME)}
        >
          <Home
            className="h-8 w-6"
            style={{ fill: menuActive === MENU_KEYS.HOME ? websiteColor : "black" }}
          />
          <span
            className="-mt-1"
            style={{ color: menuActive === MENU_KEYS.HOME ? websiteColor : "" }}
          >
            {UI_TEXT.MENU.HOME}
          </span>
        </div>

        {/* Cart */}
        <div
          className="flex flex-col items-center mt-2 cursor-pointer hover:scale-105 relative"
          onClick={() => handleMenuClick(MENU_KEYS.CART)}
        >
          <div>
            <div className="flex flex-col w-max items-center md:mb-0">
              <CartIcon
                className="h-8 w-6"
                style={{ fill: menuActive === MENU_KEYS.CART ? websiteColor : "black" }}
              />
              {totalItems > 0 && (
                <div
                  className="h-5 w-5 rounded-full bg-stepperCircle text-white flex items-center justify-center absolute ml-8"
                  style={{ fontSize: "10px" }}
                >
                  {totalItems}
                </div>
              )}
              <span
                className="-mt-1 text-center"
                style={{ color: menuActive === MENU_KEYS.CART ? websiteColor : "" }}
              >
                {UI_TEXT.MENU.CART}
              </span>
            </div>
          </div>
        </div>

        {/* Profile */}
        <div
          className="flex flex-col items-center mt-2 cursor-pointer hover:scale-105"
          style={{ color: menuActive === MENU_KEYS.PROFILE ? websiteColor : "" }}
          onClick={() => handleMenuClick(MENU_KEYS.PROFILE)}
        >
          <UserIcon
            className="h-8 w-10"
            style={{ fill: menuActive === MENU_KEYS.PROFILE ? websiteColor : "black" }}
          />
          <span className="-mt-1">{UI_TEXT.MENU.PROFILE}</span>
        </div>

        {/* FAQ */}
        <div
          className="flex flex-col items-center mt-2 cursor-pointer hover:scale-105"
          style={{ color: menuActive === MENU_KEYS.FAQ ? websiteColor : "" }}
          onClick={() => handleMenuClick(MENU_KEYS.FAQ)}
        >
          <FaqIcon
            className="h-7 w-7 mb-1"
            style={{ stroke: menuActive === MENU_KEYS.FAQ ? websiteColor : "black",  }}
          />
          <span className="-mt-1">{UI_TEXT.MENU.FAQ}</span>
        </div>

      </div>


      <LogoutConfirmModal
        isOpen={isLogoutModalOpen}
        onCancel={() => setIsLogoutModalOpen(false)}
        onConfirm={() => setIsLogoutModalOpen(false)}
      />
    </div>
  );
};

export default BottomMenu;

/* ------------------------------------------------------------------
   Profile Drawer
-------------------------------------------------------------------*/

const ProfileDrawer = ({ closeDrawer, userDetails, onLogoutClick, onFaqClick }) => {
  return (
    <div className="w-full">
      <ul className="menu p-4 md:w-[448px] w-full min-h-full bg-white text-base-content">
        {/* header */}
        <div className="flex justify-between items-center">
          <div className="space-x-3 flex items-center">
            <img
              className="w-14 h-14 rounded-full object-cover"
              loading="lazy"
              src={userDetails?.profileImage || FALLBACK_AVATAR_URL}
              alt={UI_TEXT.ALT.USER_AVATAR}
            />
            <div className="flex flex-col">
              <p className="text-neutral-800 text-base font-normal font-inter leading-snug">
                {userDetails?.common_customer_First_Name || ""}
              </p>
              <p className="text-neutral-800 text-sm font-normal font-inter leading-snug">
                {userDetails?.common_customer_Telephone || ""}
              </p>
            </div>
          </div>
          <div className="cursor-pointer" onClick={closeDrawer}>
            <img src={Cross} className="w-6 mdl:w-8" alt={UI_TEXT.ALT.CLOSE_ICON} />
          </div>
        </div>

        {/* menu */}
        <div className="pt-5 sm:h-[calc(100vh-215px)] sm:overflow-y-auto md:h-[calc(100vh-215px)] md:overflow-y-auto">
          <DrawerItem
            title={UI_TEXT.PROFILE_DRAWER.PROFILE_INFO}
            Icon={UserIcon}
            to={ProfilePage}
            onClick={closeDrawer}
            bottomborder={1}
          />
          <DrawerItem
            title={UI_TEXT.PROFILE_DRAWER.ADDRESS}
            Icon={LocationIcon}
            to={AddressPage}
            onClick={closeDrawer}
            bottomborder={1}
          />
          <DrawerItem
            title={UI_TEXT.PROFILE_DRAWER.BOOKMARKS}
            Icon={BookmarkIcon}
            to={BookmarksPage}
            onClick={closeDrawer}
            bottomborder={1}
          />
          {/* <DrawerItem
            title={UI_TEXT.PROFILE_DRAWER.BANK_CARDS}
            Icon={Card}
            to={BankcardsPage}
            onClick={closeDrawer}
            bottomborder={1}
          /> */}
          <DrawerItem
            title={UI_TEXT.PROFILE_DRAWER.PAST_ORDERS}
            Icon={PastorderIcon}
            to={PastOrdersPage}
            onClick={closeDrawer}
            bottomborder={1}
          />
          <DrawerItem
            title={UI_TEXT.PROFILE_DRAWER.MY_REWARDS}
            Icon={MyReward}
            to={RewardsPage}
            onClick={closeDrawer}
            bottomborder={1}
          />
          <DrawerItem
            title={UI_TEXT.PROFILE_DRAWER.PREFERENCE}
            Icon={HamburgerIcon}
            to={PreferencePage}
            onClick={closeDrawer}
            bottomborder={1}
          />
          <DrawerItem
            title={UI_TEXT.PROFILE_DRAWER.WALLET}
            Icon={WalletIcon}
            to={WalletPage}
            onClick={closeDrawer}
            bottomborder={1}
          />

          {/* footer */}
          <div className="absolute bottom-5 w-[90%] sm:w-[95%] md:w-[91%]">
            <DrawerItem
              title={UI_TEXT.PROFILE_DRAWER.LOGOUT}
              to="#"
              Icon={LogoutIcon}
              onClick={onLogoutClick}
            />
            <div className="border-b border-divider mt-2" />
            <div className="flex items-center justify-between mt-6 px-3">
              <p
                className="cursor-pointer text-gray-shade-11 text-sm"
                onClick={onFaqClick}
              >
                {UI_TEXT.PROFILE_DRAWER.FAQ}
              </p>
              <p
                className="cursor-pointer text-gray-shade-11 text-sm"
                onClick={onFaqClick}
              >
                {UI_TEXT.PROFILE_DRAWER.PRIVACY_POLICY}
              </p>
            </div>
          </div>
        </div>
      </ul>
    </div>
  );
};

/* ------------------------------------------------------------------
   Drawer Item
-------------------------------------------------------------------*/

const DrawerItem = ({ to, title, Icon, onClick, bottomborder = 0 }) => {
  const websiteColor = useSelector((state) => state?.onLoadData?.color);
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);

  const isActive = location.pathname === to;

  const bgColor = isActive || isHovered ? websiteColor : "";
  const textColor = isActive ? "white" : "text-zinc-700";

  return (
    <>
      <NavLink
        to={to}
        style={{
          margin: "0!important",
          backgroundColor: bgColor,
          color: isActive ? "white" : "",
        }}
        className="group flex justify-between items-center m-0 hover:fill-white py-1.5 px-2 rounded-lg mx-0 my-2"
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <>
          <div className="w-full m-0 group flex space-x-6 items-center">
            <Icon className={`${isActive ? "text-white fill-white" : ""} w-7 2xl:w-8 2xl:h-8`} />
            <div
              className={`${textColor} group-hover:text-white text-base font-medium font-inter leading-normal`}
            >
              {title}
            </div>
          </div>
          <ChevronRightIcon
            className={`${isActive ? "text-white" : ""} group-hover:text-white w-5 h-5`}
          />
        </>
      </NavLink>
      {bottomborder === 1 && <div className="border-b border-divider mx-2 mt-2" />}
    </>
  );
};
